import { useTranslation } from 'react-i18next';
import { DEFAULT_CONNECTION_FEE_CENTS } from '@evi/shared';
import formStyles from '@/components/Form.module.css';
import {
  computeHostMargin, hostMarginText, type UtilityRateEstimate,
} from '@/lib/hostMargin';
import styles from './EarnPromptCard.module.css';

/**
 * The price the earn walk opens at, quoted on every entry point so the
 * profit an owner is shown BEFORE tapping is the profit the walk's first
 * step shows. Cents per kWh, same unit as the Home Guest tariff.
 */
export const SUGGESTED_GUEST_RATE_CENTS = 30;

/**
 * "Want to earn from it?" — the one question every home-charger entry
 * point asks: Add Charger's 201, the My Home register sheet, and the
 * charger page's unenrolled slot (frictionless flow, piece 3 entry
 * points). It enrolls nothing itself. The primary button routes to the
 * earn walk at /my-chargers/:id/earn, which owns the disclaimer, Stripe
 * Express and the enroll call; the secondary routes wherever the caller
 * says. So the card carries no money path — copy and two buttons.
 *
 * The number line runs the SUGGESTED price through the same
 * computeHostMargin the charger page uses for a typed price, against the
 * owner's Home Settings electricity estimate, so no entry point can
 * promise a margin the settings form would then contradict. No estimate
 * yet → the noRate sentence (85% of the price; set a rate to see the
 * profit). A flat estimate above the host's take renders the loss
 * variant honestly — the walk is where they pick a better price. The
 * line names EVI's connection fee when the charger carries one, the
 * same way the walk and the settings form do.
 *
 * Copy is passed in, not keyed here: each caller's strings live in its
 * own namespace (myChargers / myHome) beside the page's other strings.
 * The margin sentence itself is the myChargers `detail.utilityRate.*`
 * family — hostMarginText's contract — hence this component's own
 * translator.
 */
export interface EarnPromptCardProps {
  title: string;
  /** One line under the title; omit on a surface that already said it. */
  subtitle?: string;
  /** `home_monetization.utility_rate` off the charger detail. `null` = no
   *  estimate set (the noRate line); `undefined` = the caller is still
   *  loading it, and the line is held back rather than flashing the
   *  noRate wording before the real one. */
  utilityRate: UtilityRateEstimate | null | undefined;
  /** The charger's EVI connection fee, cents (`home_monetization.
   *  connection_fee_cents`). `null` = no fee at this charger; omitted
   *  (a surface with no charger payload, e.g. the Add Charger 201) =
   *  the fee every new charger is created with. */
  connectionFeeCents?: number | null;
  /** The home's currency (`home_monetization.currency`, item 3): the
   *  line is printed in it, CA$ at a Canadian home. Absent or null reads
   *  USD, what every caller showed before. */
  currency?: string | null;
  primaryLabel: string;
  onPrimary: () => void;
  /** Both or neither: omit for the single-button variant. */
  secondaryLabel?: string;
  onSecondary?: () => void;
}

export function EarnPromptCard({
  title, subtitle, utilityRate, connectionFeeCents = DEFAULT_CONNECTION_FEE_CENTS, currency,
  primaryLabel, onPrimary, secondaryLabel, onSecondary,
}: EarnPromptCardProps) {
  const { t, i18n } = useTranslation('myChargers');
  // computeHostMargin returns null only for a non-positive price, so with
  // the suggested price the line exists whenever the rate has loaded.
  const margin = utilityRate === undefined
    ? null
    : computeHostMargin(SUGGESTED_GUEST_RATE_CENTS, utilityRate);
  const line = margin
    ? hostMarginText(margin, t, connectionFeeCents, { currency, locale: i18n.language })
    : null;

  return (
    <section className={styles.card}>
      <h2 className={styles.title}>{title}</h2>
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      {line && (
        <p className={line.loss ? styles.marginWarn : styles.margin}>
          {line.text}
        </p>
      )}
      {line && utilityRate != null && <p className={styles.subtitle}>{t('detail.utilityRate.note')}</p>}
      {/* In flow, never anchored: the page's bottom padding clears the
          pill nav, so the last button scrolls fully above it. Secondary
          leads, the way every sheet form here orders Cancel / Submit. */}
      <div className={styles.actions}>
        {secondaryLabel && onSecondary && (
          <button type="button" className={formStyles.btnSecondary} onClick={onSecondary}>
            {secondaryLabel}
          </button>
        )}
        <button type="button" className={formStyles.btnPrimary} onClick={onPrimary}>
          {primaryLabel}
        </button>
      </div>
    </section>
  );
}
